import { SxProps, Theme } from '@mui/material';

import {
  buildRowQuery,
  ROW_CONTAINER
} from '@/shared/ui/components/list-row/ListRowFrame';

import { FIELD_VALUE_DENSE_SX } from '@/shared/ui/styles/fieldTypography';

/**
 * `ROW_CONTAINER` переехал в общий каркас строки `ListRowFrame` (18976): каркас
 * единой геометрии строчных видов ОБЪЯВЛЯЕТ этот примитив, домены его не
 * заводят по-своему. Реэкспорт сохраняет имя для `CardPhotoStrip.tsx`, который
 * продолжает импортировать его отсюда (вид `photos` не переехал на каркас —
 * своя раскладка ленты превью).
 */
export { ROW_CONTAINER };

/**
 * ДОМЕННЫЙ порог «широкой» ветки актёрской строки — 420px, тот же билдер
 * (`buildRowQuery`), которым каркас требует строить любой порог, отличный от
 * его собственного внутреннего `WIDE_ROW` (см. JSDoc `ListRowFrame.tsx`).
 * Совпадение с каркасным порогом числом — историческое (актёрская строка
 * держала этот порог ДО каркаса), а не повод подставлять каркасный `WIDE_ROW`
 * напрямую: строка та же самая, но домен владеет ею сам, и сдвиг каркасного
 * внутреннего порога в будущем не должен молча утащить за собой актёрскую
 * строку. `CardPhotoStrip.tsx` продолжает импортировать `WIDE_ROW` отсюда без
 * изменений — значение строки байт-в-байт то же.
 */
export const WIDE_ROW = buildRowQuery(420);

/**
 * Имя строки ровно в одну строку: `nowrap` плюс эллипсис по краю своей колонки.
 * `minWidth: 0` обязателен — у флекс-элемента с `nowrap` автоминимум равен ширине
 * всего текста, и без него имя не сжималось бы, а выдавливало соседей из ряда.
 *
 * Индиго в цвете нет намеренно: по `visual-hierarchy.md` индиго обещает ссылку, а
 * ссылка здесь вся строка целиком, и подкрашивать внутри неё отдельное слово незачем.
 *
 * Галка подтверждения в этом режиме идёт СОСЕДНИМ флекс-элементом, а не внутри
 * текста: `overflow: hidden` уносит вместе с обрезанным текстом и галку, которая
 * сидит за ним (см. `CardNameLine`).
 */
export const ROW_NAME_SINGLE_LINE_SX: SxProps<Theme> = {
  color: 'text.primary',
  fontWeight: 600,
  fontSize: '14px',
  lineHeight: 1.4,
  minWidth: 0,
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis'
};

/**
 * Имя строки (вид `list`): ОДНА строка с эллипсисом, а не клэмп на две (18976,
 * третий заход). Клэмп на 2 строки (первый и второй заход) не спасал:
 * `overflow-clip-margin` (первый заход) протекал обрезанной строкой ВНИЗ, на
 * поля «Возраст/Город/Тип проекта» под именем; плоский `hidden` без
 * clip-margin (второй заход) убрал протечку, но у ДЛИННОГО имени, дожимающего
 * обе строки клэмпа целиком, третья строка всё равно рисовалась поверх полей
 * (замер `getBoundingClientRect` в браузере на ФИО в три слова). Одна строка
 * убирает целый класс дефектов по построению: нет второй строки — нет и
 * третьей, а высота строки перестаёт зависеть от длины имени.
 *
 * Только текст — галка подтверждения сюда не входит, `CardNameLine` рисует её
 * отдельным флекс-соседом (см. его JSDoc) сразу за именем, а не у правого края
 * колонки.
 */
export const ROW_NAME_SX: SxProps<Theme> = {
  ...ROW_NAME_SINGLE_LINE_SX,
  [WIDE_ROW]: { fontSize: '16px' }
};

/**
 * Ширина колонки под галку подтверждения в строчных видах: 18px иконки + 4px
 * её `marginLeft` (см. `VerifiedBadge`). Все строчные виды (`list`, `photos`,
 * `compact`) держат имя в ОДНУ строку и галку — СОСЕДНИМ флекс-элементом, а не
 * внутри текста (`CardNameLine`, `BADGE_SLOT_SX`); `flexShrink: 0` на этой
 * колонке отдаёт ей ровно эту ширину, а не сжимает вместе с именем.
 *
 * `CardGridTile` (сеточная плитка) — другая история: там галка остаётся
 * ИНЛАЙНОВОЙ в потоке текста (под именем фото, а не поля, клэмп там шире), и
 * эта константа для неё — только базовое число ДО добавки за больший, 24px,
 * размер бейджа (`GRID_BADGE_OVERHANG`/`GRID_BADGE_BLEED`) — не тот же приём,
 * просто общая арифметика.
 */
export const VERIFIED_BADGE_OVERHANG = 22;

/** Значение поля в строке: 12px в узкой колонке, 14px в широкой */
export const ROW_VALUE_SX: SxProps<Theme> = {
  ...FIELD_VALUE_DENSE_SX,
  [WIDE_ROW]: { fontSize: '14px' }
};
